/**
 * Premium two-or-more segment switcher — sliding pill, overflow-safe.
 * Track is forced LTR so pill translateX matches DOM order in both locales.
 * Used by ConsultationScreen header tabs and Journey about-you gender.
 */
import React, { memo, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle, type ViewStyle, type LayoutChangeEvent } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { Clickable } from './Clickable';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { DASH_STUDIO_RADIUS, androidFlatSurfaceStyle, androidScaleFont, androidScaleSize } from '../../shared/theme/studio/stackScreenShell';
import { JOURNEY_SPRING } from '../../shared/theme/studio/journeyMotion';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const TRACK_PADDING = androidScaleSize(4);
const TRACK_GAP = androidScaleSize(4);

/** Segment row min height — keep in sync with `segment` style below. */
export const SEGMENTED_MIN_HEIGHT = androidScaleSize(46);
/** Track chrome height (padding + segment). */
export const SEGMENTED_TRACK_HEIGHT = SEGMENTED_MIN_HEIGHT + TRACK_PADDING * 2;
/** Full header block below title row: outer margins + track + `ScreenHeader` `bottomContent` padding. */
export const SEGMENTED_HEADER_BLOCK_HEIGHT =
  appTheme.spacing.xs + appTheme.spacing.xs + SEGMENTED_TRACK_HEIGHT + appTheme.spacing.sm;

export type SegmentOption<T extends string = string> = {
  value: T;
  label: string;
};

type Props<T extends string = string> = {
  colors: ThemeColors;
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  style?: ViewStyle;
  testID?: string;
};

export const SegmentedControl = memo(function SegmentedControl<T extends string>({
  colors,
  options,
  value,
  onChange,
  disabled = false,
  style,
  testID,
}: Props<T>) {
  const sf = androidScaleFont;
  const styles = useMemo(() => createStyles(colors, sf), [colors, sf]);
  const [track_width, setTrackWidth] = useState(0);
  const pill_x = useSharedValue(0);

  const segment_count = options.length;
  const segment_width =
    track_width > 0 && segment_count > 0
      ? (track_width - TRACK_PADDING * 2 - TRACK_GAP * Math.max(0, segment_count - 1)) / segment_count
      : 0;

  const selected_index = Math.max(0, options.findIndex(o => o.value === value));

  useEffect(() => {
    if (segment_width <= 0 || segment_count <= 0) {
      return;
    }
    const next_x = TRACK_PADDING + selected_index * (segment_width + TRACK_GAP);
    pill_x.value = withSpring(next_x, JOURNEY_SPRING);
  }, [selected_index, segment_width, segment_count, pill_x]);

  const on_track_layout = (event: LayoutChangeEvent) => {
    setTrackWidth(event.nativeEvent.layout.width);
  };

  const pill_style = useAnimatedStyle(() => ({
    transform: [{ translateX: pill_x.value }],
    width: segment_width > 0 ? segment_width : 0,
  }));

  return (
    <View
      style={[styles.track, horizontalLtrDirectionStyle, disabled && styles.track_disabled, style]}
      testID={testID}
      onLayout={on_track_layout}>
      <Animated.View style={[styles.pill, pill_style]} pointerEvents="none" />
      {options.map(option => {
        const active = value === option.value;
        return (
          <Clickable
            key={option.value}
            onPress={() => {
              if (disabled) {
                return;
              }
              onChange(option.value);
            }}
            disabled={disabled}
            useNativePressable
            pressAnimated={false}
            haptics={!disabled}
            style={styles.segment}
            contentBorderRadius={DASH_STUDIO_RADIUS - 6}
            accessibilityRole="tab"
            accessibilityState={{ selected: active, disabled }}
            testID={testID ? `${testID}-${option.value}` : undefined}>
            <Text style={[styles.segment_text, active && styles.segment_text_active]} numberOfLines={1}>
              {option.label}
            </Text>
          </Clickable>
        );
      })}
    </View>
  );
}) as <T extends string>(props: Props<T>) => React.ReactElement;

function createStyles(c: ThemeColors, sf: (n: number) => number) {
  const idle_text = hexToRgba(c.text, 0.72);

  return StyleSheet.create({
    track: {
      flexDirection: 'row',
      padding: TRACK_PADDING,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: hexToRgba(c.text, 0.07),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.text, 0.1),
      gap: TRACK_GAP,
      position: 'relative',
      overflow: 'hidden',
      ...androidFlatSurfaceStyle(),
    },
    track_disabled: {
      opacity: 0.55,
    },
    pill: {
      position: 'absolute',
      top: TRACK_PADDING,
      bottom: TRACK_PADDING,
      left: 0,
      zIndex: 0,
      borderRadius: DASH_STUDIO_RADIUS - 6,
      backgroundColor: c.primary,
      ...Platform.select({
        ios: {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.18,
          shadowRadius: 4,
        },
        android: { elevation: 0 },
      }),
    },
    segment: {
      flex: 1,
      minWidth: 0,
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: SEGMENTED_MIN_HEIGHT,
      paddingVertical: androidScaleSize(10),
      paddingHorizontal: androidScaleSize(8),
      borderRadius: DASH_STUDIO_RADIUS - 6,
      zIndex: 2,
      backgroundColor: 'transparent',
      ...Platform.select({
        android: { elevation: 0 },
        default: {},
      }),
    },
    segment_text: {
      ...Platform.select({
        android: { includeFontPadding: false, textAlign: 'center' as const, width: '100%' as const },
        default: { textAlign: 'center' as const, width: '100%' as const },
      }),
      fontSize: sf(14),
      fontWeight: '700',
      color: idle_text,
      ...ANDROID_TEXT_COMPACT,
    },
    segment_text_active: {
      color: c.onPrimary ?? c.primaryText ?? c.white,
    },
  });
}
